import { Injectable, OnDestroy } from "@angular/core";
import { Observable, Subscription } from "rxjs";
import { IRemoteOrderGroupServiceSelectorService } from "./remoteOrderGroupServiceSelector.facade";
import { RemoteGroupOrderService } from "../../services/RemoteGroupOrder.service";
import { IGroupManageData, ILocationData } from "../../services/IRemoteGroupOrderService";


@Injectable()
export class RemoteOrderGroupServiceSelectorService implements IRemoteOrderGroupServiceSelectorService, OnDestroy {
  constructor(
    private _remoteGroupOrderService: RemoteGroupOrderService,
  ) { }

  ngOnDestroy(): void {
    const me = this;
    if (me._itemsChangedSubscription)
      me._itemsChangedSubscription.unsubscribe();
  }

  private _itemsChangedSubscription: Subscription;
  /** Location data.*/
  public get locationData$(): Observable<ILocationData> {
    return this._remoteGroupOrderService.locationData$;
  }

  /** Group management data.*/
  public get groupManageData(): IGroupManageData {
    return this._remoteGroupOrderService.groupManageData;
  }

  /**
  * Observable stream of the total price of items in the group basket.
  * @returns Total price.
  */
  public get groupBasketTotalPrice$(): Observable<number> {
    return this._remoteGroupOrderService.groupBasketTotalPrice$;
  }

  /**
  * Observable stream of the count of items in the group basket.
  * @returns An observable of the item count.
  */
  public get groupBasketItemsCount$(): Observable<number> {
    return this._remoteGroupOrderService.groupBasketItemsCount$;
  }

  /**  Verifies if the current user is the creator of the group order. */
  public get isGroupCreator() {
    const me = this;
    return me._remoteGroupOrderService.userIsGroupCreator;
  }

  /** User ID of the current user.*/
  public get userId(): number {
    return this._remoteGroupOrderService.groupManageData.userId;
  }

  /** Indicating if the state is ready */
  public get stateReady$(): Observable<boolean> {
    return this._remoteGroupOrderService.stateReady$;
  }

  /**
  * Changes the state 'Ready' of a user.
  * @param  userId - The ID of the user.
  * @param state - The new state readiness.
  */
  public async changeStateReady(userId: number, state: boolean) {
    const me = this;
    await me._remoteGroupOrderService.changeAndSetUserStateReady(userId, state);
  }

  /** All members have the state 'Ready' */
  public get allMembersAreReady$(): Observable<boolean> {
    return this._remoteGroupOrderService.allMembersAreReady$;
  }

  public async initialize() {
    const me = this;
    await me.getLocationData();
    await me.loadBasketData();
    me._itemsChangedSubscription = me._remoteGroupOrderService.itemsChanged$.subscribe(async () => {
      await me._remoteGroupOrderService.loadBasketData();
    });
  }

  private async loadBasketData(): Promise<void> {
    const me = this;
    await me._remoteGroupOrderService.loadBasketData();
  }

  private async getLocationData() {
    const me = this;
    await me._remoteGroupOrderService.getCurrentLocationData();
  }

  public get isGroupDelivery() {
    const me = this;
    return me._remoteGroupOrderService.isGroupDelivery;
  }

  public timeDisplayValue(time: string) {
    const me = this;
    return me._remoteGroupOrderService.timeDisplayValue(time);
  }

  public dayDisplayValue(day: string) {
    const me = this;
    return me._remoteGroupOrderService.dayDisplayValue(day);
  }

  public isDayToday(selectedDay: string) {
    const me = this;
    return me._remoteGroupOrderService.isDayToday(selectedDay)
  }

  public openDialogConfirmLeaveGroupCreator(callback: (stay: boolean) => void): Promise<void> {
    return this._remoteGroupOrderService.openDialogConfirmLeaveGroupCreator(callback);
  }

  public openDialogConfirmLeaveGroupMember(callback: (stay: boolean) => void): Promise<void> {
    return this._remoteGroupOrderService.openDialogConfirmLeaveGroupMember(callback);
  }

  public async leaveGroup() {
    const me = this;
    await me._remoteGroupOrderService.leaveGroup();
  }
}
